import { CELL_BUTTON, VALUE_CELL } from "@/features/data-curation/gridCells";
import type { CurationColumn, ExtractionSearchNote } from "@/features/data-curation/types";
import type { KeyboardEvent, MouseEvent } from "react";

interface EmptyGridCellProps {
  column: CurationColumn;
  /** Names the row for screen readers. */
  rowLabel: string;
  note: ExtractionSearchNote | undefined;
  selected: boolean;
  onPress: (event: MouseEvent) => void;
  onEnter: (event: MouseEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onOpenNote: () => void;
  onContextMenu?: (event: MouseEvent) => void;
}

/** A cell with no value: not found after a search, or empty because no one searched. */
export const EmptyGridCell = ({
  column,
  rowLabel,
  note,
  selected,
  onPress,
  onEnter,
  onKeyDown,
  onOpenNote,
  onContextMenu,
}: EmptyGridCellProps) => {
  const tone = column.required
    ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-zinc-200 bg-zinc-50/60 text-zinc-500";
  const label = note ? "Not found" : column.required ? "Missing required value" : "Empty";
  return (
    <td
      data-selected={selected ? "true" : undefined}
      className={`${VALUE_CELL} relative p-0 align-top ${tone} ${selected
        ? "bg-amber-100 outline outline-2 -outline-offset-2 outline-amber-400"
        : "hover:bg-zinc-100"
        }`}
    >
      <button
        type="button"
        className={`${CELL_BUTTON} min-h-8 w-full px-2 py-1.5 text-left text-2xs font-medium ${selected ? "focus-visible:before:ring-amber-500" : "focus-visible:before:ring-zinc-900"
          }`}
        aria-label={`${column.label}, ${rowLabel}: ${label}${note ? ". Show where it was searched" : ""
          }${selected ? "; selected" : ""}`}
        title={note ? `${note.note} · Right-click or double-click for details` : undefined}
        data-grid-cell
        onMouseDown={onPress}
        onMouseEnter={onEnter}
        onKeyDown={onKeyDown}
        onContextMenu={onContextMenu}
        onClick={(event) => {
          if (event.detail === 0 && note) onOpenNote();
        }}
        onDoubleClick={note ? onOpenNote : undefined}
      >
        {label}
      </button>
    </td>
  );
};
